/**
 * RBAC (Role-Based Access Control) Helper
 * This helper provides functions to check user permissions based on their role
 */

import { getLoggedinUser } from "./api_helper";

/**
 * Get the current user's role permissions
 * @returns {Object} - The user's role permissions object
 */
export const getUserPermissions = () => {
  const authUser = getLoggedinUser();
  if (!authUser) {
    return {};
  }
  
  // The user data structure may have permissions at different levels
  // Check common patterns: roleID.rolePermissions, role.rolePermissions, rolePermissions, permissions
  const user = authUser.user || authUser;
  
  const permissions = user?.roleID?.rolePermissions ||  // Backend uses roleID
                      user?.role?.rolePermissions || 
                      user?.rolePermissions || 
                      user?.permissions ||
                      authUser?.roleID?.rolePermissions ||  // Also check at root level
                      authUser?.role?.rolePermissions ||
                      authUser?.rolePermissions ||
                      authUser?.permissions ||
                      {};
  
  return permissions;
};

/**
 * Check if user has access to a specific module (has at least one permission)
 * @param {string} moduleId - The module ID to check access for
 * @returns {boolean} - True if user has access to the module
 */
export const hasModuleAccess = (moduleId) => {
  const permissions = getUserPermissions();
  const modulePermissions = permissions[moduleId];
  
  if (!modulePermissions) {
    return false;
  }
  
  // Check if any permission (read, create, update, delete) is true
  return Object.values(modulePermissions).some(value => value === true);
};

/**
 * Check if user has a specific permission on a module
 * @param {string} moduleId - The module ID
 * @param {string} permission - The permission type (read, create, update, delete)
 * @returns {boolean} - True if user has the specific permission
 */
export const hasPermission = (moduleId, permission) => {
  const permissions = getUserPermissions();
  const modulePermissions = permissions[moduleId];
  
  if (!modulePermissions) {
    return false;
  }
  
  return modulePermissions[permission] === true;
};

/**
 * Check if user can read a module
 * @param {string} moduleId - The module ID
 * @returns {boolean}
 */
export const canRead = (moduleId) => hasPermission(moduleId, 'read');

/**
 * Check if user can create in a module
 * @param {string} moduleId - The module ID
 * @returns {boolean}
 */
export const canCreate = (moduleId) => hasPermission(moduleId, 'create');

/**
 * Check if user can update in a module
 * @param {string} moduleId - The module ID
 * @returns {boolean}
 */
export const canUpdate = (moduleId) => hasPermission(moduleId, 'update');

/**
 * Check if user can delete in a module
 * @param {string} moduleId - The module ID
 * @returns {boolean}
 */
export const canDelete = (moduleId) => hasPermission(moduleId, 'delete');

/**
 * Get user's role name
 * @returns {string|null} - The user's role name or null
 */
export const getUserRoleName = () => {
  const authUser = getLoggedinUser();
  if (!authUser) {
    return null;
  }
  
  const user = authUser.user || authUser;
  return user?.roleID?.roleName ||  // Backend uses roleID
         user?.role?.roleName || 
         user?.roleName || 
         authUser?.roleID?.roleName ||  // Also check at root level
         authUser?.role?.roleName ||
         authUser?.roleName ||
         null;
};

/**
 * Check if user is super admin (has access to all modules)
 * Only "Super Admin" role has full access, not regular "Admin"
 * @returns {boolean}
 */
export const isAdmin = () => {
  const roleName = getUserRoleName();
  if (!roleName) {
    return false;
  }
  // Normalize role name: lowercase and remove spaces
  const normalizedRole = roleName.toLowerCase().replace(/\s+/g, '');
  // Only Super Admin has full access, not regular Admin
  return normalizedRole === 'superadmin';
};

/**
 * Check if RBAC is configured (user has role with permissions)
 * @returns {boolean}
 */
export const isRBACConfigured = () => {
  const authUser = getLoggedinUser();
  if (!authUser) {
    return false;
  }
  
  const user = authUser.user || authUser;
  // Check if role or permissions exist
  const hasRole = !!(user?.role || user?.roleName);
  const hasPermissions = !!(user?.role?.rolePermissions || user?.rolePermissions || user?.permissions);
  
  return hasRole || hasPermissions;
};

/**
 * Filter menu items based on user permissions
 * @param {Array} menuItems - Array of menu items
 * @returns {Array} - Filtered menu items based on user access
 */
export const filterMenuByPermissions = (menuItems) => {
  // If user is admin, return all items
  if (isAdmin()) {
    return menuItems;
  }
  
  return menuItems.filter(item => {
    // Always show header items
    if (item.isHeader) {
      return true;
    }
    
    // Check if user has access to this module
    const moduleId = item.id;
    
    // If the item has an id that matches a permission module, check access
    if (moduleId && hasModuleAccess(moduleId)) {
      return true;
    }
    
    // If item has no id or no matching permission, check if it has accessible subitems
    if (item.subItems && item.subItems.length > 0) {
      const accessibleSubItems = item.subItems.filter(subItem => {
        return hasModuleAccess(subItem.id) || hasModuleAccess(subItem.parentId);
      });
      return accessibleSubItems.length > 0;
    }
    
    // Dashboard is always accessible if user has any permissions
    if (moduleId === 'dashboard') {
      const permissions = getUserPermissions();
      return Object.keys(permissions).length > 0;
    }
    
    return false;
  });
};
